import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Hero() {
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center overflow-hidden">
      {/* Union Jack background image with overlay */}
      <div className="absolute inset-0 bg-black/70">
        <div 
          className="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-40" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1461514880624-45b9a889e76a?q=80&w=2574&auto=format&fit=crop')" }}
        ></div>
      </div>

      {/* Background overlay with pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMTYgMGgxdjJoLTF2LTJ6bS04IDBoMXYyaC0xdi0yem0tOC0xaDJ2MWgtMnYtMXptOCAwaDJ2MWgtMnYtMXptOCAwaDJ2MWgtMnYtMXpNMCA0aDF2MkgwVjR6bTAgMTZoMXYySDB2LTJ6bTAgLThoMXYySDB2LTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>

      {/* Animated circles */}
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-taekwondo-red/20 mix-blend-multiply blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-taekwondo-blue/20 mix-blend-multiply blur-3xl animate-pulse delay-700"></div>

      <div className="container mx-auto px-4 max-md:landscape:py-4 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-slide-up">
            {/* OMAC Logo */}
            <div className="mb-8">
              <picture>
                <source media="(min-width: 1024px)" srcSet="/images/logos/omac_white_large.png" />
                <source media="(min-width: 640px)" srcSet="/images/logos/omac_white_md.png" />
                <img
                  src="/images/logos/omac_white_sm.png"
                  alt="OMAC-UK Logo"
                  className="w-full max-w-[360px] mx-auto lg:mx-0 h-auto"
                />
              </picture>
            </div>
            <div className="text-taekwondo-red font-medium text-lg mb-6">
              Welcome to OMAC-UK
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-display leading-tight mb-6">
              Discover the Art of<br />
              <span className="text-taekwondo-red">Taekwondo</span> Excellence
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-lg">
              Join our Taekwondo academy in Gedling, Nottingham and embark on a journey of 
              discipline, confidence, and physical mastery for all ages.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" asChild className="bg-taekwondo-red hover:bg-taekwondo-red/90 text-white rounded-md px-8 shadow-lg">
                <Link to="/contact">
                  Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" asChild className="bg-[#024398] hover:bg-[#024398]/90 text-white rounded-md px-8 shadow-lg">
                <Link to="/schedule">
                  View Schedule
                </Link>
              </Button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">30+</div>
                <div className="text-sm text-gray-400">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">160+</div>
                <div className="text-sm text-gray-400">Active Students</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">9</div>
                <div className="text-sm text-gray-400">Expert Instructors</div>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-taekwondo-blue/30 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-taekwondo-red/20 rounded-full blur-3xl"></div>
              <div className="glass-panel rounded-2xl p-6 transform rotate-3 animate-fade-in relative">
                <img 
                  src="/images/juliet/juliet_kick.jpeg"
                  alt="Taekwondo kick" 
                  className="rounded-xl object-cover w-full h-auto shadow-2xl"
                />
                
                {/* Floating accent elements */}
                <div className="absolute -top-6 -right-6 bg-white rounded-lg shadow-lg p-4 rotate-6">
                  <div className="text-xs text-gray-500 font-medium">Classes for</div>
                  <div className="text-lg font-bold text-taekwondo-red">All Ages</div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 bg-taekwondo-blue text-white rounded-lg shadow-lg p-4 -rotate-3">
                  <div className="text-xs font-medium">Established</div>
                  <div className="text-lg font-bold">2003</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
